import React, { memo, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Users, ChevronRight } from "lucide-react";

const GameCard = memo(({ game, teamName, onGameClick, onTeamClick }) => {
  const isHome = game.location === "home";
  const opponent = isHome ? game.away_team : game.home_team;
  const opponentOrgId = isHome ? game.away_team_id : game.home_team_id;
  const teamScore = isHome ? game.home_score : game.away_score;
  const oppScore = isHome ? game.away_score : game.home_score;
  const isWin = game.result === "W";
  const isLoss = game.result === "L";

  const date = new Date(game.date);
  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const year = date.getFullYear();

  return (
    <div
      onClick={() => onGameClick(game)}
      className={`group relative bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 ${
        isWin
          ? "border-green-200 hover:border-green-300"
          : isLoss
          ? "border-red-200 hover:border-red-300"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div
        className={`absolute top-0 left-0 w-1.5 h-full rounded-l-xl ${
          isWin ? "bg-green-500" : isLoss ? "bg-red-500" : "bg-gray-400"
        }`}
      />

      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formattedDate}, {year}</span>
              <span className="text-gray-300">•</span>
              <MapPin className="w-3.5 h-3.5" />
              <span className="capitalize">
                {isHome ? "Home" : "Away"}
                {game.neutral_site && " (Neutral)"}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                {isHome ? "vs" : "@"}
              </span>
              {opponentOrgId ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTeamClick(opponentOrgId);
                  }}
                  className="text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate"
                >
                  {opponent}
                </button>
              ) : (
                <span className="text-base font-semibold text-gray-900 truncate">
                  {opponent}
                </span>
              )}
            </div>

            {game.attendance > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                <Users className="w-3 h-3" />
                <span>{game.attendance.toLocaleString()} attendance</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-1">
            <div
              className={`text-2xl font-bold tracking-tight ${
                isWin ? "text-green-600" : isLoss ? "text-red-600" : "text-gray-600"
              }`}
            >
              {teamScore} - {oppScore}
            </div>
            <div
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                isWin
                  ? "bg-green-100 text-green-700"
                  : isLoss
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {isWin ? "WIN" : isLoss ? "LOSS" : "TIE"}
            </div>
            {game.innings && game.innings !== 9 && (
              <span className="text-xs text-gray-400">{game.innings} innings</span>
            )}
          </div>
        </div>

        <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </div>
  );
});

GameCard.displayName = "GameCard";

export const TeamGames = memo(({ games, teamName }) => {
  const navigate = useNavigate();
  
  const seasons = useMemo(() => {
    if (!games || games.length === 0) return [];
    const years = [...new Set(games.map(g => g.year || new Date(g.date).getFullYear()))];
    return years.sort((a, b) => b - a);
  }, [games]);

  const [selectedSeason, setSelectedSeason] = useState(() => seasons[0] || null);

  const handleGameClick = (game) => {
    navigate(`/games/${game.year}/${game.contest_id}`);
  };

  const handleTeamClick = (orgId) => {
    navigate(`/team/${orgId}`);
  };

  if (!games || games.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">No games data available</div>
    );
  }

  const filteredGames = selectedSeason
    ? games.filter(g => (g.year || new Date(g.date).getFullYear()) === selectedSeason)
    : games;

  const sortedGames = [...filteredGames].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const wins = filteredGames.filter((g) => g.result === "W").length;
  const losses = filteredGames.filter((g) => g.result === "L").length;
  const ties = filteredGames.filter((g) => g.result === "T").length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <select
            value={selectedSeason || ""}
            onChange={(e) => setSelectedSeason(e.target.value ? Number(e.target.value) : null)}
            className="text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer hover:border-gray-300 transition-colors"
          >
            {seasons.map((season) => (
              <option key={season} value={season}>
                {season} Season
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            {filteredGames.length} Games
          </span>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-600 font-semibold">{wins}W</span>
            <span className="text-red-600 font-semibold">{losses}L</span>
            {ties > 0 && <span className="text-gray-600 font-semibold">{ties}T</span>}
          </div>
        </div>
        <span className="text-xs text-gray-400">Click any game for details</span>
      </div>

      <div className="grid gap-3">
        {sortedGames.map((game, idx) => (
          <GameCard
            key={game.contest_id || idx}
            game={game}
            teamName={teamName}
            onGameClick={handleGameClick}
            onTeamClick={handleTeamClick}
          />
        ))}
      </div>
    </div>
  );
});

TeamGames.displayName = "TeamGames";

